<template>
  <div class="funnel-step-container flex align-items-center">
    <!-- Single Next Step (or no next step yet, implying we are at a leaf or just traversal) -->
    <!-- Actually, the recursive logic shows: [Current Step] [Separator if next exists] [Next Step Component] -->

    <!-- current Step Item -->
    <div
      v-if="!hideSelf"
      class="funnel-step-item"
      :class="{ 'funnel-step-item--active': isActive }"
      @click="handleSelectFunnel(funnelStep.template)"
    >
      <span v-tooltip.bottom="funnelStepName" class="funnel-step-item__label">
        {{ funnelStepName || "" }}
      </span>
    </div>

    <!-- Separator -->
    <div
      v-if="!hideSelf && funnelStep.nextSteps.length"
      class="funnel-step-separator"
    >
      <i class="fa-light fa-chevron-right" />
    </div>

    <!-- Next Step Logic -->
    <!-- Case 1: Multiple Next Steps (Branching) -->
    <div
      v-if="funnelStep.nextSteps.length > 1"
      class="funnel-step-branch flex align-items-center"
    >
      <!-- The selected next step acts as the 'current' visible node for that branch level -->
      <!-- We need a dropdown trigger to switch branches -->
      <div
        class="funnel-step-item funnel-step-item--has-dropdown"
        :class="{ 'funnel-step-item--active': selectedNextStepIsActive }"
        @click="toggleNextStepMenu"
      >
        <span
          v-tooltip.bottom="selectedNextStep?.template?.name ?? ''"
          class="funnel-step-item__label"
        >
          {{ selectedNextStep?.template?.name || "" }}
        </span>
        <i class="fa-solid fa-chevron-down funnel-step-item__dropdown-icon" />
      </div>

      <!-- The Menu for siblings -->
      <Menu
        ref="nextStepMenuRef"
        :model="nextStepsMenuModel"
        popup
        class="teaser-menu-popup"
      />

      <!-- Continue Recursion if the selected next step has its own next steps -->
      <div
        v-if="selectedNextStep && selectedNextStep.nextSteps.length"
        class="funnel-step-separator"
      >
        <i class="fa-light fa-chevron-right" />
      </div>

      <FunnelStep
        v-if="selectedNextStep"
        :key="selectedNextStep.template?.id"
        :funnel-step="selectedNextStep"
        :is-selected="false"
        :is-root="false"
        :hide-self="true"
      />
    </div>

    <!-- Case 2: Single Next Step -->
    <template v-else-if="funnelStep.nextSteps.length === 1">
      <FunnelStep
        v-if="selectedNextStep"
        :key="selectedNextStep.template?.id"
        :funnel-step="selectedNextStep"
        :is-selected="false"
        :is-root="false"
      />
    </template>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  PropType,
  ComputedRef,
  nextTick
} from "vue";

import Menu from "primevue/menu";
import Tooltip from "primevue/tooltip";
import { MenuItem } from "primevue/menuitem";

import { useI18n } from "vue-i18n";
import { useStore } from "@/store";
import {
  activate,
  addToFunnelList,
  beforeSaveTemplate,
  checkWhatsappHashChangeAndConfirm,
  createGamePrizeTemplates,
  decodeGamePrizeTemplateIdFromRaw,
  FunnelStep,
  FunnelTemplate,
  hideLeftToolbar,
  prepareTemplate,
  restoreSmartListsOriginalCode,
  saveTemplate,
  TemplateEditorState as state,
  updateSectionsInState,
  updateTemplateConfiguration
} from "@/composables/template-editor/TemplateEditor";
import { createHistory } from "@/composables/template-editor/History";
import { getTemplateIframeDocument, isWhatsappTemplate } from "@/components/template-builder/utils/helpers";
import { HistoryType } from "@/types";

export default defineComponent({
  name: "FunnelStep",

  components: {
    Menu
  },

  directives: {
    tooltip: Tooltip
  },

  props: {
    funnelStep: {
      type: Object as PropType<FunnelStep>,
      required: true
    },
    // isSelected and isRoot might be less relevant visually now but keeping for prop compat if needed by recursive calls logic
    isSelected: {
      type: Boolean,
      required: false,
      default: false
    },
    isRoot: {
      type: Boolean,
      default: false
    },
    hideSelf: {
      type: Boolean,
      default: false
    }
  },

  setup(props) {
    const { t } = useI18n();
    const store = useStore();
    const selectedNextStep = ref<FunnelStep | null>(null);
    const nextStepMenuRef = ref();
    const funnelStepName = computed(() => {
      // For game funnel steps, use the name directly (derived from prize label)
      if (props.funnelStep.template?.isGameFunnel) {
        return props.funnelStep.template.name ?? "";
      }
      if (state.template?.originTemplateId !== undefined) {
        return state.templatesFromFunnel[
          state.template.originTemplateId as string
        ]?.find(
          item => String(item.id) === String(props.funnelStep.template?.id)
        )?.informations.name ?? "";
      }
      return props.funnelStep.template?.name ?? "";
    });

    const handleSelectFunnel = async (
      template: FunnelTemplate | null | undefined
    ) => {
      if (!template) return;

      // Handle virtual game funnel steps (template not yet created)
      if (template.isGameFunnel && template.id < 0) {
        const prizeIndex = Math.abs(template.id) - 1;
        store.commit("general/showTheSpinner");

        if (!(await beforeSaveTemplate(t))) {
          store.commit("general/hideTheSpinner");
          return;
        }

        await createHistory(HistoryType.INTERMEDIATE_POINT_OF_SAVE, true);
        updateSectionsInState(false);

        if (!(await saveTemplate())) {
          store.commit("general/hideTheSpinner");
          return;
        }

        // After save, find the real template ID from updated data-prizes / data-win-combinations
        const iframeDoc = getTemplateIframeDocument();
        const wheelEl = iframeDoc?.querySelector(".spm_widget_game_wheel");
        const slotEl = iframeDoc?.querySelector(".spm_widget_game_slot_machine");
        const gameEl = wheelEl || slotEl;
        if (gameEl) {
          try {
            let prizes: any[] = [];
            if (wheelEl) {
              prizes = JSON.parse(wheelEl.getAttribute("data-prizes") || "[]");
            } else if (slotEl) {
              prizes = JSON.parse(slotEl.getAttribute("data-win-combinations") || "[]");
            }
            const prizeData = prizes[prizeIndex];
            const templateIdRaw = prizeData?.templateId;
            const realTemplateId = decodeGamePrizeTemplateIdFromRaw(templateIdRaw);
            if (realTemplateId != null) {
              const originId = String(state.template?.originTemplateId);
              const idx =
                state.templatesFromFunnel[originId]?.findIndex(
                  item => item.id === realTemplateId
                ) ?? -1;
              if (idx !== -1) {
                await nextTick();
                await activate(
                  realTemplateId,
                  template.name,
                  template.type,
                  idx,
                  false
                );
              }
            }
          } catch (e) {
            // Failed to read prizes
          }
        }
        store.commit("general/hideTheSpinner");
        return;
      }

      if (!state.template) return;
      if (String(template.id) === String(state.template.id)) return;

      const originId = state.template.originTemplateId || String(state.template.id);
      let funnelList = state.templatesFromFunnel[originId];
      if (!funnelList) return;
      let templateFromFunnelIndex = funnelList
        .findIndex(item => String(item.id) === String(template.id));

      // Game prize templates may not be in templatesFromFunnel if the initial
      // fetch during loadGameFunnelSteps failed or was incomplete.
      // Fetch on-the-fly so the normal navigation flow can proceed.
      let onTheFlyGameTemplate: any = null;
      if (templateFromFunnelIndex === -1 && template.isGameFunnel && template.id > 0) {
        const prepared = await prepareTemplate(template.id, template.name, template.type);
        if (prepared?.template) {
          (prepared.template as any).funnel = state.template.funnel;
          (prepared.template as any).originTemplateId = originId;
          onTheFlyGameTemplate = prepared.template;
        }
      }

      if (templateFromFunnelIndex === -1 && !onTheFlyGameTemplate) {
        return;
      }

      try {
        if (!state.leftToolbar.show.widgetsPanel) {
          await hideLeftToolbar();
        }
        restoreSmartListsOriginalCode();
        updateSectionsInState(false);

        if (
          isWhatsappTemplate(state.template.type) &&
          !(await beforeSaveTemplate(t))
        ) {
          store.commit("general/hideTheSpinner");
          return;
        }

        const saveAndNavigate = async () => {
          // Create game prize templates if needed before saving
          if (await createGamePrizeTemplates()) {
            updateSectionsInState(false);
          }
          await updateTemplateConfiguration();
          await saveTemplate();
          if (
            isWhatsappTemplate(state.template?.type) &&
            state.whatsappHashChanged
          ) {
            store.commit("liveEditor/setNeedRevalidation", true);
          }
          await nextTick();

          // Resolve the index right before activate — array may have changed during save
          let activateIndex = templateFromFunnelIndex;
          if (onTheFlyGameTemplate) {
            // addToFunnelList mutates the real (non-readonly) state
            activateIndex = addToFunnelList(originId, onTheFlyGameTemplate);
          } else {
            // Re-check index for non-game templates too (index may have shifted)
            const currentList = state.templatesFromFunnel[originId];
            if (currentList) {
              const freshIdx = currentList.findIndex((t: any) => String(t.id) === String(template.id));
              if (freshIdx !== -1) {
                activateIndex = freshIdx;
              }
            }
          }

          await activate(
            template.id,
            template.name,
            template.type,
            activateIndex,
            false
          );
        };

        if (isWhatsappTemplate(state.template.type)) {
          if (!checkWhatsappHashChangeAndConfirm(saveAndNavigate)) {
            store.commit("general/hideTheSpinner");
            return;
          }
        }
        await saveAndNavigate();
      } catch (e) {
        console.error("[FunnelStep] Navigation error:", e);
      } finally {
        store.commit("general/hideTheSpinner");
      }
    };

    const handleSelectNextStepWithDropdown = (
      template: FunnelTemplate | null | undefined
    ) => {
      handleSelectFunnel(template);
    };

    const toggleNextStepMenu = (event: Event) => {
      nextStepMenuRef.value?.toggle(event);
    };

    const nextStepsMenuModel: ComputedRef<MenuItem[]> = computed(() => {
      const normalSteps = props.funnelStep.nextSteps.filter(s => !s.template?.isGameFunnel);
      const gameSteps = props.funnelStep.nextSteps.filter(s => s.template?.isGameFunnel);

      const activeId = String(state.template?.id);
      const toMenuItem = (nextStep: FunnelStep): MenuItem => ({
        label: nextStep.template?.name || "",
        class:
          String(nextStep.template?.id) === activeId || isDescendant(nextStep, activeId)
            ? "font-bold surface-100 text-primary"
            : "",
        command: async () => {
          await handleSelectFunnel(nextStep.template);
        }
      });

      // No game steps: flat list
      if (gameSteps.length === 0) {
        return normalSteps.map(toMenuItem);
      }

      // Has game steps: grouped with labels
      const groups: MenuItem[] = [];

      if (normalSteps.length > 0) {
        groups.push({
          label: t('templateBuilder.funnelStepNav.steps'),
          items: normalSteps.map(toMenuItem),
        });
      }

      groups.push({
        label: t('templateBuilder.funnelStepNav.gameResults'),
        items: gameSteps.map(toMenuItem),
      });

      return groups;
    });

    const isActive = computed(
      () => String(props.funnelStep.template?.id) === String(state.template?.id)
    );
    const selectedNextStepIsActive = computed(() => {
      const activeId = String(state.template?.id);
      return props.funnelStep.nextSteps.some(
        step => String(step.template?.id) === activeId
      );
    });

    const isDescendant = (step: FunnelStep, targetId: string): boolean => {
      if (String(step.template?.id) === targetId) return true;
      return step.nextSteps.some(child => isDescendant(child, targetId));
    };

    const pickDefaultStep = (steps: FunnelStep[]): FunnelStep => {
      // Prefer a non-game step that has children (shows deeper funnel path)
      const withChildren = steps.find(
        s => !s.template?.isGameFunnel && s.nextSteps.length > 0
      );
      return withChildren || steps[0];
    };

    const refreshActiveNextStep = (steps: FunnelStep[]) => {
      const activeId = String(state.template?.id);

      // Check if active template is a direct sibling
      const directIndex = steps.findIndex(
        nextStep => String(nextStep.template?.id) === activeId
      );
      if (directIndex !== -1) {
        // Only switch if it has children (to show its subtree)
        if (steps[directIndex].nextSteps.length > 0) {
          selectedNextStep.value = steps[directIndex];
        }
        // Leaf: don't change selectedNextStep, keep showing the deepest branch
        return;
      }

      // Ancestor match: active template is a deeper descendant of one of the next steps
      const ancestorIndex = steps.findIndex(
        nextStep => isDescendant(nextStep, activeId)
      );
      if (ancestorIndex !== -1) {
        selectedNextStep.value = steps[ancestorIndex];
        return;
      }
    };

    onMounted(() => {
      if (props.funnelStep.nextSteps.length) {
        selectedNextStep.value = pickDefaultStep(props.funnelStep.nextSteps);
        refreshActiveNextStep(props.funnelStep.nextSteps);
      }
    });

    watch(
      () => props.funnelStep,
      newFunnelStep => {
        if (newFunnelStep.nextSteps.length) {
          selectedNextStep.value = pickDefaultStep(newFunnelStep.nextSteps);
          refreshActiveNextStep(newFunnelStep.nextSteps);
        }
      },
      { deep: true }
    );

    watch(
      () => state.template?.id,
      newId => {
        if (newId && props.funnelStep.nextSteps.length) {
          refreshActiveNextStep(props.funnelStep.nextSteps);
        }
      }
    );

    return {
      t,
      selectedNextStep,
      nextStepsMenuModel,
      isActive,
      selectedNextStepIsActive,
      funnelStepName,
      handleSelectFunnel,
      toggleNextStepMenu,
      nextStepMenuRef,
      handleSelectNextStepWithDropdown
    };
  }
});
</script>

<style lang="scss" scoped>
.funnel-step-container {
  display: flex;
  align-items: center;
}

.funnel-step-item {
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: $buttonBorderRadius;
  transition: all 0.2s ease;
  border: 1px solid $solitude-light;
  border-bottom: 2px solid $solitude-light;
  color: $shuttle-grey;
  font-weight: 500;
  width: 160px; /* Reduced width */
  height: 40px; /* Enforce uniform height */
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  background-color: $white;
  flex-shrink: 0;

  &:hover {
    background-color: $alice-blue;
    color: $brand-color-secondary;
    border-color: $pattens-blue;
  }

  &--active {
    color: $brand-color-secondary;
    border-color: $pattens-blue;
    border-bottom-color: $brand-color-primary;
    font-weight: 600;
    background-color: $white;
  }

  &__label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    min-width: 0; /* Allow flex child to shrink properly */
    flex: 1;
    text-align: center;
  }

  &__dropdown-icon {
    font-size: 0.85rem;
    color: inherit;
    padding: 6px; /* Larger hit area */
    margin-right: -4px; /* Compensate for padding */
    border-radius: 50%;
    opacity: 0.6;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
      opacity: 1;
      background-color: rgba(0, 0, 0, 0.08);
      color: $brand-color-secondary;
    }
  }
}

.funnel-step-separator {
  color: $heather;
  margin: 0 0.5rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
